import { Store } from '@ngrx/store';
import { isNotNullOrUndefined, System } from 'ngx-core-services';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, mapTo, shareReplay, startWith, switchMap, delay } from 'rxjs/operators';

import { SystemsState, requestToAddASystem, selectSystemMetadata, updateCurrentObservers } from '../../store';

export interface IReduxBlock {
  system$: BehaviorSubject<System|undefined>;
  _system?: System;
  system?: System;
  refresh$: Subject<void>;
  _refresh$: Observable<boolean>;
  __refresh$?: Observable<boolean>;
  _instanceId$: Observable<string>;
  metadata$?: Subject<void>;
  instanceId?: string;
  readonly store: Store<SystemsState>;
}

export interface IBlockReduxOptions {
  requestAddSystemWithSelection: boolean;
  requestAddSystemWithSubscription: boolean;
}

const defaultOptions: IBlockReduxOptions = {
  requestAddSystemWithSelection: true,
  requestAddSystemWithSubscription: true,
};

export function CreateBlockReduxSupport(opts: IBlockReduxOptions = defaultOptions) {
  return <T extends { new (...args: any[]): {} }>(target: T) => {
    const originalNgOnDestroy = target.prototype.ngOnDestroy;
    const componentName = target.name;

    target.prototype.metadata$ = new Subject();
    Object.defineProperty(target.prototype, '_refresh$', {
      get: function () {
        if (!this.__refresh$) {
          this.__refresh$ = defer(() => combineLatest([
            this.refresh$.pipe(startWith(null)),
            this.metadata$,
          ])).pipe(
            mapTo(true),
            debounceTime(300),
            switchMap(() => {
              /**
               * @important This is necessary, because of the
               * providers: [{ provide: Store, useClass: CustomStore }],
               * where there is a DEFAULT_DEBOUNCE_MS, it only passes the last action
               */
              return of(false).pipe(
                delay(100),
                startWith(true),
              );
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
          );
        }
        return this.__refresh$;
      },
      set: function () {},
      configurable: true,
      enumerable: true
    });

    Object.defineProperty(target.prototype, '_instanceId$', {
      get: function () {
        return defer(() => of(this.instanceId));
      },
      set: function () {},
      configurable: true,
      enumerable: true
    });

    let metadataSubscription: Subscription | undefined;
    const originalSystemSetter = Object.getOwnPropertyDescriptor(target.prototype, 'system')?.set;
    Object.defineProperty(target.prototype, 'system', {
      get: function () {
        return this._system;
      },
      set: function (value: System) {
        if (this._system?.uid && this._system.uid !== value.uid) {
          this.store.dispatch(updateCurrentObservers({ systemId: this._system.uid, observerKey: `${componentName}`, operation: 'remove' }));
        }

        if (value.uid && this._system?.uid !== value.uid) {
          const skipSub = (value as any)?.__skipSubscription === true;
          this.store.dispatch(requestToAddASystem({
            systemId: value.uid,
            system: value,
            withAutomaticSelection: opts.requestAddSystemWithSelection,
            withAutomaticSubscription: skipSub ? false : opts.requestAddSystemWithSubscription,
            comment: `${componentName}`,
            observerKey: `${componentName}`,
          }));
        }

        if (value.uid && this._system?.uid !== value.uid) {
          metadataSubscription?.unsubscribe();
          metadataSubscription = this.store.select(selectSystemMetadata(value.uid)).pipe(
            isNotNullOrUndefined(),
          ).subscribe(() => this.metadata$.next());
        }

        this._system = value;
        this.system$.next(value);

        if (originalSystemSetter) {
          originalSystemSetter.call(this, value);
        }
      },
      configurable: true,
      enumerable: true
    });

    target.prototype.ngOnDestroy = function () {
      if (this._system?.uid) {
        this.store.dispatch(updateCurrentObservers({ systemId: this._system.uid, observerKey: `${componentName}`, operation: 'remove' }));
      }

      metadataSubscription?.unsubscribe();

      if (originalNgOnDestroy) {
        originalNgOnDestroy.apply(this);
      }
    };

    return class extends target {
      instanceId: string;

      constructor(...args: any[]) {
        super(...args);
        this.instanceId = Math.random().toString(36).substring(7);
      }
    };
  };
}
